import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { fetchAccounts as apiFetchAccounts } from '@app/application/account-mapping/api'
import {
  fetchReconciliationTemplates as apiFetchReconciliationTemplates,
  fetchExports as apiFetchExports,
} from '@app/application/communication/api'
import {
  formatMentionAccounts,
  formatMentionReconTemplates,
  formatMentionExports,
} from '@app/application/communication/utils'
import type { Account } from '@app/application/account-mapping/shared-types'
import type {
  ReconciliationTemplate,
  Export,
} from '@app/application/communication/types'
import { SfLog } from '@app/adapters/logger'

export const useMentionablesStore = defineStore('mentionables', () => {
  const companyId = ref<string | null>(null)
  const ledgerId = ref<string | null>(null)
  const accounts = ref<Account[]>([])
  const reconciliationTemplates = ref<ReconciliationTemplate[]>([])
  const exports = ref<Export[]>([])
  const isLoadingAccounts = ref(false)
  const isLoadingReconTemplates = ref(false)
  const isLoadingExports = ref(false)

  async function initialize(
    newCompanyId: string,
    newLedgerId: string
  ): Promise<void> {
    await setCompany(newCompanyId)
    await setLedger(newLedgerId)
  }

  async function setCompany(newCompanyId: string): Promise<void> {
    if (newCompanyId !== companyId.value) {
      companyId.value = newCompanyId
      await Promise.all([
        fetchAccounts(),
        fetchReconciliationTemplates(),
        fetchExports(),
      ])
    }
  }

  async function setLedger(newLedgerId: string): Promise<void> {
    if (newLedgerId !== ledgerId.value) {
      ledgerId.value = newLedgerId
      await Promise.all([fetchReconciliationTemplates(), fetchExports()])
    }
  }

  async function fetchAccounts(): Promise<void> {
    isLoadingAccounts.value = true
    try {
      const response = (await apiFetchAccounts({
        companyId: companyId.value,
      })) as Account[] | null

      if (!response) return

      accounts.value = response
    } catch (error) {
      accounts.value = []
      SfLog.warn('Failed to fetch mentionable accounts', {
        extra: { error },
      })
    } finally {
      isLoadingAccounts.value = false
    }
  }

  async function fetchReconciliationTemplates(): Promise<void> {
    if (!ledgerId.value) return
    isLoadingReconTemplates.value = true
    try {
      const response = await apiFetchReconciliationTemplates({
        companyId: companyId.value ?? undefined,
        ledgerId: ledgerId.value ?? undefined,
      })

      if (!response) return

      reconciliationTemplates.value = response
    } catch (error) {
      reconciliationTemplates.value = []
      SfLog.warn('Failed to fetch mentionable reconciliation templates', {
        extra: { error },
      })
    } finally {
      isLoadingReconTemplates.value = false
    }
  }

  async function fetchExports(): Promise<void> {
    if (!ledgerId.value) return
    isLoadingExports.value = true
    try {
      const response = await apiFetchExports({
        companyId: companyId.value ?? undefined,
        ledgerId: ledgerId.value ?? undefined,
      })

      if (!response) return

      exports.value = response
    } catch (error) {
      exports.value = []
      SfLog.warn('Failed to fetch mentionable exports', {
        extra: { error },
      })
    } finally {
      isLoadingExports.value = false
    }
  }

  const mentionAccounts = computed(() => {
    if (!companyId.value || !ledgerId.value) {
      return []
    }

    return formatMentionAccounts(
      accounts.value,
      companyId.value,
      ledgerId.value
    )
  })

  const mentionReconTemplates = computed(() => {
    if (!companyId.value || !ledgerId.value) {
      return []
    }

    return formatMentionReconTemplates(
      reconciliationTemplates.value,
      companyId.value,
      ledgerId.value
    )
  })

  const mentionExports = computed(() => {
    return formatMentionExports(exports.value)
  })

  return {
    initialize,
    mentionAccounts,
    mentionReconTemplates,
    mentionExports,
    isLoadingAccounts,
    isLoadingReconTemplates,
    isLoadingExports,
  }
})
